import { cn } from "@/lib/utils";
import { useState } from "react";
import { SidebarCategory } from "./sidebar-category";

// ============================================
// 타입 정의
// ============================================

interface SidebarPanelProps {
  /** 카테고리 아이콘 */
  icon: React.ReactNode;
  /** 카테고리 라벨 */
  label: string;
  /** 아이콘 회전 각도 (deg) */
  iconRotate?: number;
  /** 패널 열림 상태 (controlled mode) */
  isOpen?: boolean;
  /** 상태 변경 핸들러 (controlled mode) */
  onOpenChange?: (isOpen: boolean) => void;
  /** 기본 열림 상태 (uncontrolled mode) */
  defaultOpen?: boolean;
  /** 메뉴 아이템들 */
  children?: React.ReactNode;
  className?: string;
}

// ============================================
// 스타일 정의
// ============================================

const containerStyles = cn(
  "flex flex-col items-start w-full px-2 pt-3 pb-3",
  "bg-white border-b border-line-border-primary",
);

// ============================================
// SidebarPanel 컴포넌트
// ============================================

export const SidebarPanel = ({
  icon,
  label,
  iconRotate = 0,
  isOpen: controlledIsOpen,
  onOpenChange,
  defaultOpen = true,
  children,
  className,
}: SidebarPanelProps) => {
  // 내부 상태 (uncontrolled mode용)
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  // controlled vs uncontrolled 판단
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    if (isControlled) {
      onOpenChange?.(!isOpen);
    } else {
      setInternalIsOpen((prev) => !prev);
    }
  };

  return (
    <div className={cn(containerStyles, className)}>
      {/* 카테고리 헤더 */}
      <SidebarCategory icon={icon} rotate={iconRotate} onClick={handleToggle}>
        {label}
      </SidebarCategory>

      {/* 메뉴 아이템들 (CSS Grid 애니메이션) */}
      <div
        className={cn(
          "grid w-full transition-[grid-template-rows,opacity,margin] duration-200",
          isOpen
            ? "grid-rows-[1fr] opacity-100 mt-2"
            : "grid-rows-[0fr] opacity-0 mt-0 pointer-events-none",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-2">{children}</div>
        </div>
      </div>
    </div>
  );
};

SidebarPanel.displayName = "SidebarPanel";

export default SidebarPanel;
